import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef
} from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { DragErrorBoundary } from '@/components/collaboration/DragErrorBoundary';
import { DragGhostCard } from '@/components/collaboration/DragGhostCard';
import { useBoardContext } from '@/contexts/BoardContext';
import { useAuth } from '@/hooks/useAuth';
import { useTimeSync } from '@/hooks/useTimeSync';
import { supabase } from '@/integrations/supabase/client';
import {
  createDragLogger,
  DragStateManager,
  PositionBroadcastLimiter,
  ConflictResolver,
  validateRealtimeUpdate,
  ChannelHealthMonitor
} from '@/utils/dragStateUtils';
import type { DragStateUser, DragPosition } from '@/utils/types';
import type { Tables } from '@/integrations/supabase/types';

// Export types for other components to use
export type { DragStateUser, DragPosition };

type CardType = Tables<'cards'>;

interface DragState {
  [boardId: string]: DragStateUser[];
}

interface DragStateContextType {
  dragState: DragState;
  updateDragState: (
    cardId: string,
    targetColumnId: string,
    dragPosition: DragPosition | null,
    isDragging: boolean
  ) => Promise<void>;
  clearDragState: (cardId: string) => void;
  startDrag: (cardId: string, sourceColumnId: string, position: DragPosition) => void;
  updateDrag: (cardId: string, targetColumnId: string | null, position: DragPosition) => void;
  endDrag: (cardId: string, targetColumnId?: string | null) => void;
  connectionHealth: 'connected' | 'disconnected' | 'reconnecting';
  forceCleanupAllDrags: () => void;
}

const DragStateContext = createContext<DragStateContextType | undefined>(
  undefined
);

interface DragStateProviderProps {
  children: React.ReactNode;
  boardId: string;
}

export const DragStateProvider: React.FC<{ children: React.ReactNode; boardId: string }> = ({ 
  children, 
  boardId 
}) => {
  const [dragState, setDragState] = useState<DragState>({});
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);
  const [connectionHealth, setConnectionHealth] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');
  const { user } = useAuth();
  const { cards } = useBoardContext();
  const { isTimeSynced } = useTimeSync();
  const logger = useRef(createDragLogger('DragStateProvider')).current;
  const dragStateManager = useRef(new DragStateManager()).current;
  const positionLimiter = useRef(new PositionBroadcastLimiter()).current;
  const conflictResolver = useRef(new ConflictResolver()).current;
  const channelHealthMonitor = useRef(new ChannelHealthMonitor()).current;
  const lastReconnectAttempt = useRef(0);
  const isMounted = useRef(false);

  // **PHASE 4: Track drag termination states**
  const pendingTerminations = useRef(new Set<string>()).current;
  
  // Configuration constants - Enhanced for smoother termination
  const RECONNECT_COOLDOWN = 10000;
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 3000;
  const TERMINATION_GRACE_PERIOD = 500; // **PHASE 1: Grace period for termination**

  // Ref to track retry attempts
  const retryCount = useRef(0);

  // Convert cards array to Map for compatibility with existing ghost card logic
  const cardsMap = new Map(cards?.map(card => [card.id, card]) || []);

  // **PHASE 1: Graceful drag termination broadcast**
  const broadcastDragTermination = useCallback(
    (cardId: string, targetColumnId: string | null, reason: 'completed' | 'cancelled' = 'completed') => {
      if (!user || !channel) {
        logger.warning('Termination broadcast aborted: missing user or channel');
        return;
      }

      const userId = user.id;
      const terminationKey = `${userId}-${cardId}`;
      
      logger.info('🏁 Broadcasting drag termination', { cardId, userId, reason });

      // **PHASE 1: Add termination to pending set**
      pendingTerminations.add(terminationKey);

      channel.send({
        type: 'broadcast',
        event: 'drag_termination',
        payload: {
          user_id: userId,
          user_name: user.user_metadata?.full_name || 'Anonymous',
          card_id: cardId,
          board_id: boardId,
          target_column_id: targetColumnId,
          reason,
          status: 'drag_ending', // **PHASE 4: Enhanced status**
          updated_at: new Date().toISOString()
        }
      });

      // **PHASE 3: Clean up after grace period**
      setTimeout(() => {
        pendingTerminations.delete(terminationKey);
        logger.info('🧹 Termination grace period completed', { cardId, userId });
      }, TERMINATION_GRACE_PERIOD);
    },
    [user, boardId, channel, logger, pendingTerminations]
  );

  // **PHASE 2: Persist critical drag events (start/end) to database**
  const persistDragEvent = useCallback(
    async (
      cardId: string,
      sourceColumnId: string,
      targetColumnId: string,
      isDragging: boolean,
      eventType: 'start' | 'end'
    ) => {
      if (!user || !isTimeSynced) {
        logger.warning('Drag event persistence aborted: user or time not synced', {
          user: !!user,
          isTimeSynced,
          eventType
        });
        return;
      }

      const userId = user.id;

      try {
        logger.info(`📝 Persisting drag ${eventType} event to database`, { cardId, userId });
        
        // **PHASE 3: Ensure broadcast happens before DB write for end events**
        if (eventType === 'end') {
          broadcastDragTermination(cardId, targetColumnId, 'completed');
          // **PHASE 3: Small delay to ensure broadcast is sent**
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        const { data, error } = await supabase
          .from('drag_state')
          .upsert(
            {
              user_id: userId,
              card_id: cardId,
              board_id: boardId,
              source_column_id: sourceColumnId,
              target_column_id: targetColumnId,
              drag_position: null,
              is_active: isDragging,
              updated_at: new Date().toISOString()
            },
            { onConflict: 'user_id, card_id, board_id' }
          );

        if (error) {
          logger.error(`Failed to persist drag ${eventType} event`, error);
          throw error;
        }

        logger.success(`📝 Drag ${eventType} event persisted successfully`, { cardId, userId });
        
        // **PHASE 4: Send completion confirmation for end events**
        if (eventType === 'end' && channel) {
          setTimeout(() => {
            channel.send({
              type: 'broadcast',
              event: 'drag_completed',
              payload: {
                user_id: userId,
                card_id: cardId,
                board_id: boardId,
                status: 'drag_completed',
                updated_at: new Date().toISOString()
              }
            });
          }, 100);
        }
      } catch (err: any) {
        logger.error(`Error persisting drag ${eventType} event`, err);
      }
    },
    [user, boardId, isTimeSynced, logger, broadcastDragTermination, channel]
  );

  // Broadcast position updates via channels only
  const broadcastPosition = useCallback(
    (cardId: string, targetColumnId: string | null, position: DragPosition) => {
      if (!user || !channel) {
        logger.warning('Position broadcast aborted: missing user or channel');
        return;
      }

      const userId = user.id;
      const userCardKey = `${userId}-${cardId}`;

      // Rate limit position broadcasts
      if (!positionLimiter.canBroadcast(userCardKey)) {
        return;
      }

      logger.info('📍 Broadcasting position update via channel', { cardId, userId, position });

      channel.send({
        type: 'broadcast',
        event: 'position_update',
        payload: {
          user_id: userId,
          user_name: user.user_metadata?.full_name || 'Anonymous',
          card_id: cardId,
          board_id: boardId,
          target_column_id: targetColumnId,
          drag_position: position,
          is_active: true,
          status: 'dragging', // **PHASE 4: Add status**
          updated_at: new Date().toISOString()
        }
      });
    },
    [user, boardId, channel, positionLimiter, logger]
  );

  // **PHASE 1: Enhanced cleanup function with termination awareness**
  const cleanupDragState = useCallback((cardId: string, userId?: string, isGraceful: boolean = false) => {
    const targetUserId = userId || user?.id;
    if (!targetUserId) return;

    const terminationKey = `${targetUserId}-${cardId}`;
    
    // **PHASE 1: Check if this is a pending graceful termination**
    if (isGraceful || pendingTerminations.has(terminationKey)) {
      logger.info('🧹 Graceful cleanup initiated', { cardId, userId: targetUserId, isGraceful });
    } else {
      logger.info('🧹 Standard cleanup initiated', { cardId, userId: targetUserId });
    }

    setDragState(prevDragState => {
      const boardState = prevDragState[boardId] || [];
      const updatedBoardState = boardState.filter(
        dragUser => !(dragUser.user_id === targetUserId && dragUser.card_id === cardId)
      );
      
      const hasChanges = updatedBoardState.length !== boardState.length;
      if (hasChanges) {
        logger.success('🧹 Drag state cleaned up', { cardId, userId: targetUserId });
      }
      
      return { ...prevDragState, [boardId]: updatedBoardState };
    });
  }, [user?.id, boardId, logger, pendingTerminations]);

  // Function to start drag tracking
  const startDrag = useCallback((cardId: string, sourceColumnId: string, position: DragPosition) => {
    logger.info('🚀 Starting drag tracking', { cardId, sourceColumnId });
    
    // Update local state immediately for responsiveness
    if (user) {
      setDragState(prevDragState => {
        const boardState = prevDragState[boardId] || [];
        const existingUserIndex = boardState.findIndex(
          dragUser => dragUser.user_id === user.id && dragUser.card_id === cardId
        );

        let updatedBoardState: DragStateUser[];

        if (existingUserIndex > -1) {
          updatedBoardState = [...boardState];
          updatedBoardState[existingUserIndex] = {
            ...updatedBoardState[existingUserIndex],
            target_column_id: sourceColumnId,
            drag_position: position,
            is_active: true,
            updated_at: new Date().toISOString()
          };
        } else {
          updatedBoardState = [
            ...boardState,
            {
              user_id: user.id,
              user_name: user.user_metadata?.full_name as string,
              card_id: cardId,
              board_id: boardId,
              target_column_id: sourceColumnId,
              drag_position: position,
              is_active: true,
              updated_at: new Date().toISOString()
            }
          ];
        }

        return { ...prevDragState, [boardId]: updatedBoardState };
      });
    }

    // Persist drag start event to database
    persistDragEvent(cardId, sourceColumnId, sourceColumnId, true, 'start');
  }, [user, boardId, persistDragEvent, logger]);

  // Function to update drag position
  const updateDrag = useCallback((cardId: string, targetColumnId: string | null, position: DragPosition) => {
    if (!user) return;

    logger.info('📍 Updating drag position', { cardId, targetColumnId, position });

    // Update local state immediately
    setDragState(prevDragState => {
      const boardState = prevDragState[boardId] || [];
      const existingUserIndex = boardState.findIndex(
        dragUser => dragUser.user_id === user.id && dragUser.card_id === cardId
      );

      if (existingUserIndex > -1) {
        const updatedBoardState = [...boardState];
        updatedBoardState[existingUserIndex] = {
          ...updatedBoardState[existingUserIndex],
          target_column_id: targetColumnId,
          drag_position: position,
          updated_at: new Date().toISOString()
        };

        return { ...prevDragState, [boardId]: updatedBoardState };
      }

      return prevDragState;
    });

    // Broadcast position via channel (high-frequency, ephemeral)
    broadcastPosition(cardId, targetColumnId, position);
  }, [user, boardId, broadcastPosition, logger]);

  // **PHASE 1: Enhanced end drag with graceful termination**
  const endDrag = useCallback((cardId: string, targetColumnId?: string | null) => {
    if (!user) return;

    logger.info('🏁 Ending drag tracking with graceful termination', { cardId, targetColumnId });

    // **PHASE 1: Immediate graceful cleanup**
    cleanupDragState(cardId, user.id, true);

    // Find source column for persistence
    const card = cards?.find(c => c.id === cardId);
    const sourceColumnId = card?.column_id || targetColumnId || '';
    
    // **PHASE 3: Persist drag end event (which will broadcast termination first)**
    persistDragEvent(cardId, sourceColumnId, targetColumnId || '', false, 'end');
  }, [user, boardId, cards, persistDragEvent, cleanupDragState, logger]);

  const updateDragState = useCallback(
    async (
      cardId: string,
      targetColumnId: string,
      dragPosition: DragPosition | null,
      isDragging: boolean
    ) => {
      if (!user || !isTimeSynced) {
        logger.warning('UpdateDragState aborted: user or time not synced', {
          user: !!user,
          isTimeSynced
        });
        return;
      }

      const userId = user.id;
      const userCardKey = `${userId}-${cardId}`;

      // Generate a unique operation ID
      const operationId = dragStateManager.generateOperationId(
        userId,
        cardId,
        isDragging ? 'update' : 'end'
      );

      // Check if this operation has already been processed
      if (dragStateManager.isOperationProcessed(operationId)) {
        logger.warning('Skipping duplicate operation', { operationId, cardId });
        return;
      }

      // Rate limit position updates
      if (isDragging && dragPosition && !positionLimiter.canBroadcast(userCardKey)) {
        return;
      }

      // Mark drag end
      if (!isDragging) {
        dragStateManager.markDragEnded(userCardKey, operationId);
      }

      // Find the source column for this card
      const card = cards?.find(c => c.id === cardId);
      const sourceColumnId = card?.column_id || targetColumnId;

      // Optimistically update the local state
      setDragState(prevDragState => {
        const boardState = prevDragState[boardId] || [];
        const existingUserIndex = boardState.findIndex(
          dragUser => dragUser.user_id === userId && dragUser.card_id === cardId
        );

        let updatedBoardState: DragStateUser[];

        if (existingUserIndex > -1) {
          // Update existing drag state
          updatedBoardState = [...boardState];
          updatedBoardState[existingUserIndex] = {
            ...updatedBoardState[existingUserIndex],
            target_column_id: targetColumnId,
            drag_position: dragPosition,
            is_active: isDragging,
            updated_at: new Date().toISOString()
          };
        } else {
          // Add new drag state
          updatedBoardState = [
            ...boardState,
            {
              user_id: userId,
              user_name: user.user_metadata?.full_name as string,
              card_id: cardId,
              board_id: boardId,
              target_column_id: targetColumnId,
              drag_position: dragPosition,
              is_active: isDragging,
              updated_at: new Date().toISOString()
            }
          ];
        }

        return { ...prevDragState, [boardId]: updatedBoardState };
      });

      try {
        // Persist the drag state to the database - convert DragPosition to JSON
        const { data, error } = await supabase
          .from('drag_state')
          .upsert(
            {
              user_id: userId,
              card_id: cardId,
              board_id: boardId,
              source_column_id: sourceColumnId, // Add required source_column_id
              target_column_id: targetColumnId,
              drag_position: dragPosition as any, // Convert to JSON
              is_active: isDragging,
              updated_at: new Date().toISOString()
            },
            { onConflict: 'user_id, card_id, board_id' }
          );

        if (error) {
          // Check for a conflict error
          if (error.code === '409') {
            logger.warning('Conflict detected during drag state update', {
              cardId,
              userId,
              error
            });
            await conflictResolver.resolveConflict({
              user_id: userId,
              card_id: cardId,
              board_id: boardId,
              target_column_id: targetColumnId,
              drag_position: dragPosition,
              is_active: isDragging
            });
          } else {
            logger.error('Failed to upsert drag state', error, {
              cardId,
              userId,
              operationId
            });
          }
          throw error;
        } else {
          // Mark the operation as processed
          dragStateManager.markOperationProcessed(operationId);
          logger.success('Drag state updated', {
            cardId,
            userId,
            operationId,
            data
          });
        }
      } catch (err: any) {
        logger.error('Error updating drag state', err, {
          cardId,
          userId,
          operationId
        });
      }
    },
    [user, boardId, isTimeSynced, logger, dragStateManager, positionLimiter, conflictResolver, cards]
  );

  // Function to clear drag state for a card
  const clearDragState = useCallback(
    (cardId: string) => {
      setDragState(prevDragState => {
        const boardState = prevDragState[boardId] || [];
        const updatedBoardState = boardState.filter(
          dragUser => dragUser.card_id !== cardId
        );
        return { ...prevDragState, [boardId]: updatedBoardState };
      });
    },
    [setDragState, boardId]
  );

  // Function to force cleanup all drags
  const forceCleanupAllDrags = useCallback(() => {
    setDragState({});
    dragStateManager.reset();
  }, [dragStateManager]);

  // **PHASE 3: Enhanced channel setup with improved termination handling**
  useEffect(() => {
    if (!supabase || !user || !isTimeSynced || !boardId || !isMounted.current) {
      logger.warning('Channel setup aborted: missing dependencies', {
        supabase: !!supabase,
        user: !!user,
        isTimeSynced,
        boardId
      });
      return;
    }

    // Reset retry count on successful channel setup
    retryCount.current = 0;

    // Function to create and set up the channel
    const setupChannel = async () => {
      // Check if a channel already exists and leave it
      if (channel) {
        logger.info('Leaving existing channel');
        await channel.unsubscribe();
      }

      logger.info('Creating new channel', { boardId });
      const newChannel = supabase.channel(`drag_state_board_${boardId}`, {
        config: {
          broadcast: { ack: false }
        }
      });

      newChannel
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'drag_state',
          filter: `board_id=eq.${boardId}`
        }, async payload => {
          if (!payload.new) {
            logger.warning('Received null payload', { payload });
            return;
          }

          // Type assertion for the realtime payload
          const record = payload.new as {
            user_id: string;
            card_id: string;
            board_id: string;
            target_column_id: string | null;
            drag_position: any;
            is_active: boolean;
            updated_at: string;
            user_name?: string | null;
          };

          const validationResult = validateRealtimeUpdate(record, user.id);

          if (!validationResult.isValid) {
            logger.warning('Invalid realtime update', {
              reason: validationResult.reason,
              record
            });
            return;
          }

          setDragState(prevDragState => {
            const boardState = prevDragState[boardId] || [];
            const existingUserIndex = boardState.findIndex(
              dragUser =>
                dragUser.user_id === record.user_id &&
                dragUser.card_id === record.card_id
            );

            let updatedBoardState: DragStateUser[];

            if (existingUserIndex > -1) {
              // Update existing drag state
              updatedBoardState = [...boardState];
              updatedBoardState[existingUserIndex] = {
                ...updatedBoardState[existingUserIndex],
                target_column_id: record.target_column_id,
                drag_position: record.drag_position ? 
                  (record.drag_position as unknown as DragPosition) : 
                  null,
                is_active: record.is_active,
                updated_at: record.updated_at
              };
            } else {
              // Add new drag state
              updatedBoardState = [
                ...boardState,
                {
                  user_id: record.user_id,
                  user_name: record.user_name || 'Anonymous',
                  card_id: record.card_id,
                  board_id: record.board_id,
                  target_column_id: record.target_column_id,
                  drag_position: record.drag_position ? 
                    (record.drag_position as unknown as DragPosition) : 
                    null,
                  is_active: record.is_active,
                  updated_at: record.updated_at
                }
              ];
            }

            return { ...prevDragState, [boardId]: updatedBoardState };
          });
        })
        .on('broadcast', { event: 'position_update' }, (payload: any) => {
          const broadcastData = payload.payload;
          if (!broadcastData || broadcastData.user_id === user.id) {
            return; // Skip own broadcasts
          }

          logger.info('📍 Received position broadcast', broadcastData);
          
          // Update local state with broadcast position
          setDragState(prevDragState => {
            const boardState = prevDragState[boardId] || [];
            const existingUserIndex = boardState.findIndex(
              dragUser =>
                dragUser.user_id === broadcastData.user_id &&
                dragUser.card_id === broadcastData.card_id
            );

            let updatedBoardState: DragStateUser[];

            if (existingUserIndex > -1) {
              updatedBoardState = [...boardState];
              updatedBoardState[existingUserIndex] = {
                ...updatedBoardState[existingUserIndex],
                target_column_id: broadcastData.target_column_id,
                drag_position: broadcastData.drag_position,
                is_active: broadcastData.is_active,
                updated_at: broadcastData.updated_at
              };
            } else {
              updatedBoardState = [
                ...boardState,
                {
                  user_id: broadcastData.user_id,
                  user_name: broadcastData.user_name || 'Anonymous',
                  card_id: broadcastData.card_id,
                  board_id: broadcastData.board_id,
                  target_column_id: broadcastData.target_column_id,
                  drag_position: broadcastData.drag_position,
                  is_active: broadcastData.is_active,
                  updated_at: broadcastData.updated_at
                }
              ];
            }

            return { ...prevDragState, [boardId]: updatedBoardState };
          });
        })
        // **PHASE 1: Handle drag termination broadcasts**
        .on('broadcast', { event: 'drag_termination' }, (payload: any) => {
          const terminationData = payload.payload;
          if (!terminationData || terminationData.user_id === user.id) {
            return; // Skip own terminations
          }

          logger.info('🏁 Received drag termination broadcast', terminationData);
          
          // **PHASE 1: Graceful cleanup for remote user**
          setTimeout(() => {
            cleanupDragState(terminationData.card_id, terminationData.user_id, true);
          }, 100); // Small delay to allow UI to prepare
        })
        // **PHASE 4: Handle drag completion confirmations**
        .on('broadcast', { event: 'drag_completed' }, (payload: any) => {
          const completionData = payload.payload;
          if (!completionData || completionData.user_id === user.id) {
            return;
          }

          logger.success('✅ Received drag completion confirmation', completionData);
          
          // Final cleanup confirmation
          setTimeout(() => {
            cleanupDragState(completionData.card_id, completionData.user_id, true);
          }, 50);
        })
        .subscribe(async status => {
          if (status === 'SUBSCRIBED') {
            logger.success('✅ Channel subscribed', { boardId });
            setConnectionHealth('connected');
            
            // Fetch initial drag state from the database
            const { data, error } = await supabase
              .from('drag_state')
              .select('*')
              .eq('board_id', boardId);

            if (error) {
              logger.error('Failed to fetch initial drag state', error);
            } else {
              // Initialize local state with fetched data
              const initialState: DragStateUser[] = data.map(record => ({
                user_id: record.user_id,
                user_name: record.user_name || 'Anonymous',
                card_id: record.card_id,
                board_id: record.board_id,
                target_column_id: record.target_column_id,
                drag_position: record.drag_position ? 
                  (record.drag_position as unknown as DragPosition) : 
                  null,
                is_active: record.is_active,
                updated_at: record.updated_at
              }));
              setDragState(prevState => ({ ...prevState, [boardId]: initialState }));
            }
          } else if (status === 'CHANNEL_ERROR') {
            channelHealthMonitor.onConnectionError();
            setConnectionHealth('disconnected');
            logger.error('❌ Channel error', { boardId, status });
          } else if (status === 'CLOSED') {
            setConnectionHealth('disconnected');
            logger.warning('⚠️ Channel closed', { boardId, status });
          }
        });

      setChannel(newChannel);
    };

    // Attempt to set up the channel
    setupChannel();

    // Cleanup function to unsubscribe from the channel
    return () => {
      if (channel) {
        logger.info('Unsubscribing from channel', { boardId });
        channel.unsubscribe();
      }
    };
  }, [user, boardId, setDragState, isTimeSynced, logger, cleanupDragState]);

  // **PHASE 3: Improved health management with less aggressive reconnection**
  useEffect(() => {
    const handleReconnect = async () => {
      if (!channelHealthMonitor.shouldReconnect()) {
        logger.warning('⚠️ Max reconnect attempts reached. Switching to graceful degradation.');
        // **PHASE 3: Graceful degradation instead of giving up**
        setConnectionHealth('disconnected');
        return;
      }

      const now = Date.now();
      if (now - lastReconnectAttempt.current < RECONNECT_COOLDOWN) {
        logger.info('🔄 Reconnect in cooldown. Skipping.');
        return;
      }

      lastReconnectAttempt.current = now;
      channelHealthMonitor.onReconnectAttempt();
      setConnectionHealth('reconnecting');

      const delay = channelHealthMonitor.getReconnectDelay();
      logger.info(`🔄 Attempting reconnect in ${delay}ms. Attempt: ${retryCount.current + 1}/${MAX_RETRIES}`);

      setTimeout(() => {
        // Re-trigger the channel setup effect by updating a dependency
        retryCount.current++;
      }, delay);
    };

    // Subscribe to health changes
    channelHealthMonitor.addHealthCallback(isHealthy => {
      if (!isHealthy) {
        handleReconnect();
      }
    });

    return () => {
      channelHealthMonitor.removeHealthCallback(handleReconnect);
    };
  }, [channelHealthMonitor, logger]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      dragStateManager.cleanupOldOperations();
      positionLimiter.cleanup();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [dragStateManager, positionLimiter]);

  // Reset state on boardId change
  useEffect(() => {
    dragStateManager.reset();
    positionLimiter.reset();
    channelHealthMonitor.reset();
    setDragState({});
  }, [boardId, dragStateManager, positionLimiter, channelHealthMonitor]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <DragStateContext.Provider value={{ 
      dragState, 
      updateDragState, 
      clearDragState, 
      startDrag, 
      updateDrag, 
      endDrag, 
      connectionHealth, 
      forceCleanupAllDrags 
    }}>
      <DragErrorBoundary>
        {children}
        {/* **PHASE 1: Enhanced ghost card rendering with termination awareness** */}
        {Object.entries(dragState).map(([currentBoardId, dragUsers]) =>
          dragUsers
            .filter(dragUser => 
              dragUser.is_active && 
              dragUser.card_id && 
              cardsMap.has(dragUser.card_id) &&
              dragUser.user_id !== user?.id // Only show other users' ghost cards
            )
            .map(dragUser => {
              const card = cardsMap.get(dragUser.card_id!);
              if (!card || !dragUser.drag_position) return null;
              
              const terminationKey = `${dragUser.user_id}-${dragUser.card_id}`;
              const isTerminating = pendingTerminations.has(terminationKey);
              
              logger.info('👻 Rendering ghost card', { 
                cardId: card.id, 
                userId: dragUser.user_id,
                isTerminating
              });
              
              return (
                <DragErrorBoundary key={`ghost-${dragUser.user_id}-${dragUser.card_id}`}>
                  <DragGhostCard
                    card={card}
                    dragUser={dragUser}
                    position={dragUser.drag_position}
                    isOverColumn={!!dragUser.target_column_id}
                    onCleanup={() => cleanupDragState(dragUser.card_id, dragUser.user_id, isTerminating)}
                  />
                </DragErrorBoundary>
              );
            })
        )}
      </DragErrorBoundary>
    </DragStateContext.Provider>
  );
};

// Custom hook to use the drag state context
export const useDragStateContext = (): DragStateContextType => {
  const context = useContext(DragStateContext);
  if (!context) {
    throw new Error('useDragStateContext must be used within a DragStateProvider');
  }
  return context;
};
